import { Controller } from "@hotwired/stimulus"
import { errorDetail, formatDate, getJSON, sendJSON } from "../api.js"
import { applyAct } from "../icons.js"

// Konto → „Zgody”: stan zgody na kontakt (tylko odczyt) i przełącznik zgody marketingowej.
// Odczyt: GET /api/users/me/ → consents[] ({kind, given, version, date, current}; given/version/date = null, gdy brak decyzji).
// Zapis: POST /api/users/me/marketing-consent/ {given} → {kind, given, version, date}; serwer dopisuje wiersz tylko przy zmianie.
const BADGE = {
  granted: ["ls-badge--sent", "● udzielona"],
  refused: ["ls-badge--new", "nie udzielona"],
  none: ["ls-badge--new", "brak decyzji"],
}

export default class extends Controller {
  static targets = [
    "contactBadge", "contactMeta",
    "marketingBadge", "marketingMeta",
    "toggleBtn", "saved", "error",
  ]
  static values = {
    meUrl: String,     // GET /api/users/me/
    toggleUrl: String, // POST /api/users/me/marketing-consent/
  }

  async connect() {
    this.consents = { contact: null, marketing: null }
    this.loaded = false
    this.pending = false
    await this.load()
  }

  async load() {
    let me
    try {
      me = await getJSON(this.meUrlValue)
    } catch {
      this.#error("Nie udało się wczytać zgód.")
      return
    }
    for (const consent of me?.consents ?? []) this.consents[consent.kind] = consent
    this.loaded = true
    this.#render()
  }

  async toggle() {
    if (this.pending || !this.loaded) return
    this.pending = true
    this.#busy(true)
    this.#error(null)
    this.#saved(false)
    const given = this.consents.marketing?.given !== true
    const res = await sendJSON("POST", this.toggleUrlValue, { given })
    this.pending = false
    this.#busy(false)
    if (!res.ok) {
      this.#error(errorDetail(res.data, "Nie udało się zapisać zgody. Spróbuj ponownie."))
      return
    }
    this.consents.marketing = { ...this.consents.marketing, ...res.data, kind: "marketing" }
    this.#render()
    this.#saved(true)
  }

  // --- prywatne ---

  #render() {
    this.#paint(
      this.consents.contact,
      this.hasContactBadgeTarget ? this.contactBadgeTarget : null,
      this.hasContactMetaTarget ? this.contactMetaTarget : null,
    )
    this.#paint(
      this.consents.marketing,
      this.hasMarketingBadgeTarget ? this.marketingBadgeTarget : null,
      this.hasMarketingMetaTarget ? this.marketingMetaTarget : null,
    )
    if (!this.hasToggleBtnTarget) return
    const granted = this.consents.marketing?.given === true
    // Row action: opting in is green / check, withdrawing is red / x-circle (label + icon + colour flip together).
    applyAct(this.toggleBtnTarget, granted
      ? { label: "Wycofaj zgodę marketingową", iconName: "x-circle", variant: "danger" }
      : { label: "Wyraź zgodę marketingową", iconName: "check", variant: "success" })
    this.toggleBtnTarget.disabled = false
  }

  #paint(consent, badge, meta) {
    const state = consent?.given === true ? "granted" : consent?.given === false ? "refused" : "none"
    const [cls, label] = BADGE[state]
    if (badge) {
      badge.className = `ls-badge ${cls}`
      badge.textContent = label
    }
    if (meta) {
      meta.textContent = consent?.version ? `wersja ${consent.version} · ${formatDate(consent.date)}` : ""
    }
  }

  #busy(on) {
    if (this.hasToggleBtnTarget) this.toggleBtnTarget.disabled = on
  }

  #saved(on) {
    if (this.hasSavedTarget) this.savedTarget.hidden = !on
  }

  #error(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = message === null
    if (message !== null) this.errorTarget.textContent = message
  }
}
